import { Grid, Typography, Button, Box } from '@material-ui/core';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import FormHelperText from '@material-ui/core/FormHelperText';
import React, { useState, useEffect, useRef } from 'react';
import { HomeDetails, PropertyUsageType } from '../../ssm/ssm.types';
import { useTranslation } from '../../../hooks/useTranslation';
import Dropdown from '../../dropdown';
import { CustomerMailingAddress } from '../../customer-mailing-address';
import { WizardFormProps } from '../../utility/wizard/wizard.types';
import { AlternateAddress } from '../../alternate-address';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import colors from '../../../themes/main-colors';
import clsx from 'clsx';
import { CheckCircle } from '@material-ui/icons';
import PhoneTextField from '../../phone-text-field';
import GoPaperLessIcon from '../../../static/goPaperLessIcon.svg';
import MailBillIcon from '../../../static/mailBillIcon.svg';
import TextLink from '../../text-link';
import ROUTES from '../../../routes';
import { convertValidationRules } from '../../../hooks/useFormState';
import { validatePropertyUsage } from '../common/SSMMoveService.rules';
import {
  validatePhone,
  validateNameField,
  validateRequiredAndValidCharacters,
  validateZipCode,
  validateCanadianZipCode,
} from '../../../util/form-validation';
import { QasModal } from '../../qas';
import {
  SuggestedAddress,
  PropertyUsage,
} from '../../../__generated__/pge-types';
import { statesAndProvincesGrouping } from '../../utility/state-dropdown';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import useAuth from '../../../hooks/useAuth';
import { MobChangeWarningModal } from '../start/MobChangeWarning/MobChangeWarning';
import { navigate } from 'gatsby';
import GreenFutureChoice from './GreenFutureChoice';
import useRenewablePower from '../../../hooks/useRenewablePower';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ssmHomeContainer: {
      '& .MuiSelect-outlined.MuiSelect-outlined': {
        backgroundColor: '#f7f8f7',
      },
      '& .MuiFormHelperText-contained': {
        backgroundColor: '#f7f8f7',
        margin: 0,
        padding: '0 14px',
      },
      '& .MuiInputBase-input.MuiOutlinedInput-input': {
        backgroundColor: '#f7f8f7',
      },
    },
    fullWidth: {
      maxWidth: '415px',
      margin: '0 auto',
    },
    subTitleHolder: {
      fontWeight: 'bold',
      margin: theme.spacing(0, 0, 2, 0),
    },
    btnHolder: {
      minWidth: '180px',
      border: `1px solid ${colors.fordGray}`,
      color: colors.fordGray,
      fontSize: '20px',
      textAlign: 'center',
      padding: theme.spacing(2, 0),
      '& svg': {
        position: 'absolute',
        width: '18px',
        height: '18px',
        top: '5px',
        right: '5px',
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: '170px',
      },
    },
    selectedBox: {
      backgroundColor: colors.backgroundLightBlue,
      color: colors.brightBlue,
      boxShadow: `0px 5px 5px ${colors.boxShadowBlack}`,
      '&:hover': {
        backgroundColor: colors.backgroundLightBlue,
        color: colors.brightBlue,
      },
    },
    blueBox: {
      backgroundColor: colors.backgroundLightBlue,
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.5),
      '& .MuiTextField-root .MuiFormHelperText-contained.Mui-error': {
        backgroundColor: colors.backgroundLightBlue,
      },
    },
    billingBtnWrap: {
      minWidth: '180px',
      border: `1px solid ${colors.fordGray}`,
      fontSize: '20px',
      textAlign: 'center',
      padding: theme.spacing(2, 0),
      position: 'relative',
      [theme.breakpoints.up('sm')]: {
        height: '127px',
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: '170px',
      },
    },
    paperLessBtnWrap: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        position: 'static',
        width: '36px',
        height: '40px',
        marginBottom: theme.spacing(1),
      },
    },
    dropdownItemFontFamily: {
      fontFamily: 'Arial,sans-serif',
    },
    marginTop: {
      marginTop: '16px',
    },
    stayingBehind: {
      '& h2': {
        fontSize: '1.125rem',
        letterSpacing: '0.360px',
      },
    },
  }),
);

export const SSMHomeDetailsForm = ({
  formState,
  componentProps,
}: WizardFormProps<HomeDetails>) => {
  const classes = useStyles();
  const { t, inlineRichT } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { wrapWithLoader } = useWrapWithLoader();
  const {
    isSSMMoveFlow,
    isSSMStart,
    customerEmail,
    startServiceAddress,
    moveServiceAddress,
    mobNumber,
    qasData,
    setQasData,
    setHomeDetails,
    setIsRenewablesEligible,
    isRenewablesEligible,
  } = componentProps;

  const { customer } = isAuthenticated
    ? useAccountCustomer()
    : { customer: { email: customerEmail } };

  const {
    eligibilityData: renewablesEligibilityData,
    renewableData,
    loading: isRenewableDataLoading,
  } = useRenewablePower({ ignoreNoAccounts: true });
  useEffect(() => {
    if (renewablesEligibilityData) {
      setIsRenewablesEligible(
        renewablesEligibilityData?.renewablesAccountEligibility.eligibility ===
          'Eligible',
      );
    }
    if (isSSMMoveFlow && renewableData) {
      void formState.setValue(
        'enrollInGreenFuture',
        renewableData?.getRenewables?.isGreenSourceEnrolled,
      );
    }
  }, [renewablesEligibilityData, renewableData, isRenewableDataLoading]);

  const [
    displayMobChangeWarningModal,
    setDisplayMobChangeWarningModal,
  ] = useState<boolean>(false);
  const [regNumber, setRegNumber] = useState<string | undefined>();
  const mailingAddressSectionId = 'service-move-mailing-address-section';

  const scrollToMailingAddressRef = useRef(false);

  useEffect(() => {
    if (scrollToMailingAddressRef.current) {
      scrollToMailingAddressRef.current = false;
      document.getElementById(mailingAddressSectionId)?.scrollIntoView();
    }
  }, [scrollToMailingAddressRef.current]);

  const handleRegisteredMobileNumberBlur = (
    e: React.MouseEvent<HTMLInputElement>,
  ) => {
    if (Boolean(mobNumber)) {
      e.persist();
      const _regNumber = e.currentTarget.value.replace(/\D+/g, '') || '';
      if (
        _regNumber !== regNumber &&
        _regNumber.length === 10 &&
        _regNumber !== mobNumber.replace(/\D+/g, '')
      ) {
        setDisplayMobChangeWarningModal(true);
      }
    }
    void formState.props('registeredMobileNumber').onBlur();
  };

  const handleMobChangeWarningModal = () => {
    setRegNumber(formState.values.registeredMobileNumber);
    setDisplayMobChangeWarningModal(false);
  };

  const formatMoveServiceAddress = () => {
    const addressLine1 = moveServiceAddress?.addressLine1?.split(',')[0].trim();
    const city = moveServiceAddress?.addressLine1?.split(',')[1].trim();
    const postal = moveServiceAddress?.addressLine1?.split(',')[2].trim();
    const formattedAddress = {
      addressLine1,
      city,
      postal,
    };
    return formattedAddress;
  };
  const completeAddress = isSSMStart
    ? startServiceAddress
    : formatMoveServiceAddress();

  return (
    <>
      <Grid
        className={clsx(
          classes.fullWidth,
          classes.ssmHomeContainer,
          isSSMStart && classes.marginTop,
        )}
      >
        <Grid data-testid="property-use-dropdown">
          <Typography
            data-testid="property-usage-title"
            className={classes.subTitleHolder}
            variant="h4"
          >
            {t('HOW_WILL_YOU_USE_THIS_PROPERTY')}
          </Typography>
          <Dropdown
            value={formState.values.propertyUsage}
            style={{ width: '100%' }}
            error={Boolean(formState.errors.propertyUsage)}
            label={t('PLEASE_SELECT_AN_OPTION')}
            selectionList={[
              {
                value: PropertyUsageType.Own,
                label: (
                  <span className={classes.dropdownItemFontFamily}>
                    {inlineRichT('OWNER_LIVING_IN_PROPERTY')}
                  </span>
                ),
              },
              {
                value: PropertyUsageType.TNT,
                label: (
                  <span className={classes.dropdownItemFontFamily}>
                    {inlineRichT('TENANT_RENTING_THE_PROPERTY')}
                  </span>
                ),
              },
              {
                value: PropertyUsageType.LLD,
                label: (
                  <span className={classes.dropdownItemFontFamily}>
                    {inlineRichT('LANDLORD_NEEDING_SERVICE_BETWEEN_TENANTS')}
                  </span>
                ),
              },
              {
                value: PropertyUsageType.FNF,
                label: (
                  <span className={classes.dropdownItemFontFamily}>
                    {inlineRichT('FRIEND/FAMILY_PAYING_ON_BEHALF_OF_TENANT')}
                  </span>
                ),
              },
            ]}
            data-testid="property-usage-dropdown"
            onChange={async e => {
              await formState.setValue('propertyUsage', e.target.value);
            }}
          />
          <FormHelperText style={{ color: colors.errorRed }}>
            {formState.errors.propertyUsage}
          </FormHelperText>
        </Grid>

        {/* Green Future Card section */}
        <Grid data-testid="green-future-choice-section">
          <GreenFutureChoice
            isEnrolled={!!formState.values.enrollInGreenFuture}
            isRenewablesEligible={isRenewablesEligible}
            isLoading={isRenewableDataLoading}
            onEnrollmentChange={async (checked: boolean) => {
              await formState.setValue('enrollInGreenFuture', checked);
            }}
          />
        </Grid>

        {/* Billing Preference section */}
        <Grid
          style={{ marginTop: '16px' }}
          data-testid="billing-preference-section"
        >
          <Typography
            data-testid="billing-preference-title"
            className={classes.subTitleHolder}
            variant="h4"
          >
            {t('BILLING_PREFERENCE')}
          </Typography>
          <Grid container justify="space-between">
            <Button
              className={clsx(
                classes.billingBtnWrap,
                classes.btnHolder,
                formState.values.goPaperLess ? classes.selectedBox : '',
              )}
              onClick={async () => {
                await formState.setValue('goPaperLess', true);
              }}
            >
              <Box className={classes.paperLessBtnWrap}>
                <GoPaperLessIcon />
                <Typography>{t('GO_PAPERLESS')}</Typography>
              </Box>
              {formState.values.goPaperLess && (
                <CheckCircle htmlColor={colors.shoreGreen} />
              )}
            </Button>
            <Button
              className={clsx(
                classes.billingBtnWrap,
                classes.btnHolder,
                !formState.values.goPaperLess ? classes.selectedBox : '',
              )}
              onClick={async () => {
                await formState.setValue('goPaperLess', false);
              }}
            >
              <Box className={classes.paperLessBtnWrap}>
                <MailBillIcon />
                <Typography>{t('MAIL_BILL')}</Typography>
              </Box>
              {!formState.values.goPaperLess && (
                <CheckCircle htmlColor={colors.shoreGreen} />
              )}
            </Button>
          </Grid>
          <Grid className={classes.blueBox}>
            {formState.values.goPaperLess && (
              <Typography variant="body2">
                {customer && customer.email
                  ? inlineRichT('SSM_PAPERLESS_BILL_MESSAGE', {
                      email: customer.email,
                    })
                  : ''}
              </Typography>
            )}
            {!formState.values.goPaperLess && (
              <Typography variant="body2">
                {t('SSM_MAILING_ADDRESS_MESSAGE')}
              </Typography>
            )}
          </Grid>
        </Grid>
        {/* Mailing Address section */}
        <Grid
          style={{ marginTop: '16px' }}
          data-testid="mailing-address-selector"
        >
          <Typography
            data-testId="mailing-address-title"
            className={classes.subTitleHolder}
            variant="h4"
          >
            {t('WILL_THIS_BE_YOUR_MAILING_ADDRESS')}
          </Typography>
          <Grid container justify="space-between">
            <Button
              className={clsx(
                classes.btnHolder,
                formState.values.sameAsServiceAddress
                  ? classes.selectedBox
                  : '',
              )}
              onClick={async () => {
                await formState.setValue('sameAsServiceAddress', true);
                await formState.setValue('mailQasVerified', false);
              }}
            >
              {t('YES')}
              {formState.values.sameAsServiceAddress && (
                <CheckCircle htmlColor={colors.shoreGreen} />
              )}
            </Button>
            <Button
              className={clsx(
                classes.btnHolder,
                !formState.values.sameAsServiceAddress
                  ? classes.selectedBox
                  : '',
              )}
              data-testid="not-mailing-address"
              onClick={async () => {
                await formState.setValue('sameAsServiceAddress', false);
                document
                  .getElementById('service-move-mailing-address-section')
                  ?.scrollIntoView();
              }}
            >
              {t('NO')}
              {!formState.values.sameAsServiceAddress && (
                <CheckCircle htmlColor={colors.shoreGreen} />
              )}
            </Button>
            <Typography style={{ marginTop: 16 }} variant="body2">
              {t('WHY_WE_NEED_MAILING_ADDRESS')}
            </Typography>
          </Grid>
        </Grid>
        <Grid data-testid="customer-info-form">
          <CustomerMailingAddress
            id={mailingAddressSectionId}
            useAltMailingAddress={!!!formState.values.sameAsServiceAddress}
            setUseAltMailingAddressForSSM={() => {
              if (formState.values.sameAsServiceAddress) {
                void formState.setValue('sameAsServiceAddress', false);
              } else {
                void formState.setValue('sameAsServiceAddress', true);
              }
            }}
            sameAddressText={
              <p style={{ display: 'block' }}>
                {t('SAME_AS_SERVICE_ADDRESS')}:{' '}
                <strong>
                  {[
                    completeAddress?.values?.address ||
                      completeAddress?.addressLine1 ||
                      '',
                    ', ',
                    completeAddress?.values?.city ||
                      completeAddress?.city ||
                      '',
                    ' ',
                    completeAddress?.values?.zip ||
                      completeAddress?.postal ||
                      '',
                  ].join('')}
                </strong>
              </p>
            }
          />
          {!formState.values.sameAsServiceAddress && (
            <AlternateAddress form={formState} />
          )}
        </Grid>

        {/* Receive Notifications section */}
        <Grid
          data-testid="receive-notification-section"
          style={{ marginTop: '16px' }}
        >
          <Typography
            data-testId="receive-notification-title"
            className={classes.subTitleHolder}
            variant="h4"
          >
            {t('RECEIVE_NOTIFICATIONS')}
          </Typography>
          <Grid container justify="space-between">
            <Button
              className={clsx(
                classes.btnHolder,
                formState.values.shouldReceiveNotification
                  ? classes.selectedBox
                  : '',
              )}
              onClick={async () => {
                await formState.setValue('shouldReceiveNotification', true);
              }}
            >
              {t('YES')}
              {formState.values.shouldReceiveNotification && (
                <CheckCircle htmlColor={colors.shoreGreen} />
              )}
            </Button>
            <Button
              className={clsx(
                classes.btnHolder,
                !formState.values.shouldReceiveNotification
                  ? classes.selectedBox
                  : '',
              )}
              data-testid="notMailingAddressBtn"
              onClick={async () => {
                await formState.setValue('shouldReceiveNotification', false);
              }}
            >
              {t('NO')}
              {!formState.values.shouldReceiveNotification && (
                <CheckCircle htmlColor={colors.shoreGreen} />
              )}
            </Button>
          </Grid>
        </Grid>

        {/* Receive Notification Blue Box */}
        <Grid data-testid="mobile-number-section" className={classes.blueBox}>
          <Typography variant="body2">
            {inlineRichT('SSM_MOVE_RECEIVE_NOTIFICATION_TITLE_TEXT')}
          </Typography>
          <PhoneTextField
            name={'primaryPhone'}
            id="ssmMobilePhoneInfo"
            data-testid="ssm-mobile-info"
            autoComplete={'telephone'}
            label={t('MOBILE_PHONE_NUMBER')}
            {...formState.props('registeredMobileNumber')}
            onBlur={handleRegisteredMobileNumberBlur}
          />
          <Typography variant="body2">
            {t('ENROLL_IN_TEXT')}
            <TextLink
              onClick={e => {
                e.preventDefault();
                window.open(ROUTES.PRIVACY_POLICY, '_blank');
              }}
            >
              {t('PRIVACY_POLICY_AND_SECURITY')}
            </TextLink>
          </Typography>
          <Typography variant="body2">
            {t('SSM_MOVE_RECEIVE_NOTIFICATION_SUBTITLE_TEXT')}
          </Typography>
        </Grid>
      </Grid>
      {qasData && (
        <>
          <QasModal
            results={qasData.results}
            onChange={async (address: SuggestedAddress) => {
              setQasData(null);
              await Promise.all([
                formState.setValue('mailCity', address.city),
                formState.setValue('mailState', address.state),
                formState.setValue('mailAddress', address.addressLine1),
                formState.setValue('mailZip', address.postal),
                await formState.setValue('mailQasVerified', true),
              ]);
            }}
            address={qasData.params}
            onSearch={(params, results) => setQasData({ params, results })}
            onEdit={() => {
              scrollToMailingAddressRef.current = true;

              setQasData(null);
            }}
            onUseEntered={async () => {
              await formState.setValue('mailQasVerified', false);
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              wrapWithLoader(formState.submit(() => setQasData(null)))();
              scrollToMailingAddressRef.current = true;
              setQasData(null);
              setHomeDetails({ ...formState.values, mailQasVerified: false });
              isSSMStart
                ? void navigate(ROUTES.SSM_START_CONFIRM)
                : void navigate(ROUTES.SSM_MOVE_TRANSFER_PEOPLE_TO_SERVICE);
            }}
          />
        </>
      )}
      <MobChangeWarningModal
        isOpen={displayMobChangeWarningModal}
        handleClose={handleMobChangeWarningModal}
      />
    </>
  );
};

export const createSSMMoveHomeDetailsFormAdapter = () => {
  return {
    toFormState: (initial: HomeDetails) => ({
      propertyUsage: initial.propertyUsage ?? undefined,
      goPaperLess: initial.goPaperLess ?? true,
      registeredMobileNumber: initial.registeredMobileNumber ?? '',
      sameAsServiceAddress: initial.sameAsServiceAddress ?? true,
      shouldReceiveNotification: initial.shouldReceiveNotification ?? true,
      mailAddress: initial.mailAddress ?? '',
      mailCity: initial.mailCity ?? '',
      mailCountry: initial.mailCountry ?? '',
      mailQasVerified: initial.mailQasVerified ?? false,
      mailState: initial.mailState ?? '',
      mailZip: initial.mailZip ?? '',
      stayingBehind: initial.stayingBehind ?? 'No',
      enrollInGreenFuture: initial.enrollInGreenFuture ?? false,
      inCareOf: initial.inCareOf ?? '',
    }),
    fromFormState: (state: HomeDetails) => {
      return {
        propertyUsage: state.propertyUsage,
        goPaperLess: state.goPaperLess,
        registeredMobileNumber: state.registeredMobileNumber,
        sameAsServiceAddress: state.sameAsServiceAddress,
        shouldReceiveNotification: state.shouldReceiveNotification,
        mailAddress: state.mailAddress,
        mailCity: state.mailCity,
        mailCountry: state.mailCountry,
        mailQasVerified: state.mailQasVerified,
        mailState: state.mailState,
        mailZip: state.mailZip,
        enrollInGreenFuture: state.enrollInGreenFuture,
        stayingBehind: state.stayingBehind,
        inCareOf: state.inCareOf,
      };
    },
  };
};

export const createHomeDetailsValidateFunction = () =>
  convertValidationRules<{
    propertyUsage: PropertyUsage;
    goPaperLess: boolean;
    registeredMobileNumber: string;
    sameAsServiceAddress: boolean;
    shouldReceiveNotification: boolean;
    mailAddress: string;
    mailState: string;
    mailCity: string;
    mailZip: string;
    inCareOf: string;
  }>(context => {
    const { values } = context;
    const { t } = useTranslation();
    return {
      propertyUsage: validatePropertyUsage,
      registeredMobileNumber: values.shouldReceiveNotification
        ? validatePhone
        : () => null,
      mailAddress: !values.sameAsServiceAddress
        ? validateRequiredAndValidCharacters.bind(
            null,
            t('STREET_ADDRESS').toLowerCase(),
          )
        : () => null,
      mailCity: !values.sameAsServiceAddress
        ? validateRequiredAndValidCharacters.bind(null, t('CITY').toLowerCase())
        : () => null,
      inCareOf: !values.sameAsServiceAddress
        ? validateRequiredAndValidCharacters.bind(
            null,
            t('IN_CARE_OF').toLowerCase(),
          )
        : () => null,
      mailState: !values.sameAsServiceAddress
        ? validateNameField.bind(null, t('STATE_PROVINCE_LABEL'))
        : () => null,
      mailZip: !values.sameAsServiceAddress
        ? statesAndProvincesGrouping.canada.includes(values.mailState)
          ? validateCanadianZipCode
          : validateZipCode
        : () => null,
    };
  });
